import { Component } from '@theme/component';

/**
 * Köttbutiken: hämtar Shopifys rekommendationer till sektionen Rekommenderade produkter
 * (sections/kb-rekommenderade.liquid) och byter ut korten när sektionen närmar sig skärmen.
 *
 * Grundprodukt i ordning: produktsidans produkt, kundens senast visade produkt (om Personligt är på,
 * Horizons localStorage viewedProducts) och sektionens valda grundprodukt. Utan grundprodukt, eller
 * om Shopify inte har några rekommendationer, ligger reservkollektionen kvar (eller sektionen förblir dold).
 */
class KbRekommenderade extends Component {
  #observer = new IntersectionObserver(
    (entries, observer) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      this.#hamta();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.kalla !== 'rekommendationer' || this.dataset.utford === 'true') return;

    // En dold sektion (ingen reserv att visa) syns aldrig för observern: hämta när webbläsaren har tid
    if (this.hidden) {
      const vanta = window.requestIdleCallback ?? ((/** @type {() => void} */ fn) => setTimeout(fn, 200));
      vanta(() => this.#hamta());
      return;
    }
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  /** @returns {{ id: string, personlig: boolean }} */
  #grundprodukt() {
    const { sidansProduktId, produktId, personlig } = this.dataset;
    if (sidansProduktId) return { id: sidansProduktId, personlig: false };

    if (personlig === 'true') {
      try {
        const visade = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
        const senast = Array.isArray(visade) ? visade.find(Boolean) : null;
        if (senast) return { id: String(senast), personlig: true };
      } catch {
        // Ingen åtkomst till localStorage: använd grundprodukten
      }
    }

    return { id: produktId || '', personlig: false };
  }

  async #hamta() {
    const { id, personlig } = this.#grundprodukt();
    if (!id) return;

    const { url, sectionId, intent, antal } = this.dataset;
    const adress = `${url}?product_id=${encodeURIComponent(id)}&limit=${antal}&section_id=${sectionId}&intent=${intent}`;

    try {
      const svar = await fetch(adress);
      if (!svar.ok) return;

      const html = new DOMParser().parseFromString(await svar.text(), 'text/html');
      const nyLista = html.querySelector('[data-kb-rek-lista]');
      const lista = this.querySelector('[data-kb-rek-lista]');
      if (!nyLista || !lista || Number(nyLista.getAttribute('data-antal')) === 0) return;

      lista.innerHTML = nyLista.innerHTML;
      lista.setAttribute('data-antal', nyLista.getAttribute('data-antal') || '0');
      lista.setAttribute('data-bytt', '');
      lista.scrollLeft = 0;
      this.dataset.utford = 'true';
      this.hidden = false;

      if (personlig) {
        const personligText = this.querySelector('[data-kb-rek-personlig]');
        if (personligText instanceof HTMLElement) personligText.hidden = false;
      }

      const karusell = /** @type {(HTMLElement & { uppdatera?: () => void }) | null} */ (
        this.querySelector('kb-karusell')
      );
      await customElements.whenDefined('kb-karusell');
      karusell?.uppdatera?.();
    } catch (fel) {
      console.warn('Köttbutiken: kunde inte hämta rekommendationer', fel);
    }
  }
}

if (!customElements.get('kb-rekommenderade')) {
  customElements.define('kb-rekommenderade', KbRekommenderade);
}
